import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('taskType');
const taskTypeProducts: ProductKeys[] = Array.from(
	new Set<ProductKeys>([...salesforceProducts, ...products3PFromConfig]),
);

const fetchDynamicTaskTypeOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const taskTypes = result.filters.find((f) => f.name === 'taskTypes');
		if (!taskTypes || taskTypes.values.length === 0) {
			return [];
		}

		return taskTypes.values.map((taskType) => ({
			value: taskType.name,
			trackingKey: taskType.name,
			queryParamValue: taskType.name,
			isDefault: false,
			label: taskType.label ?? taskType.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch (e) {
		return [];
	}
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchDynamicTaskTypeOptions(config);
	const selected = new Set(queryParams);
	const result = allOptions.filter((option) => selected.has(option.queryParamValue));
	return result;
};

export const filter: CreatedSelectFilter<'taskType'> = createMultiSelectFilter({
	id: 'taskType',
	requiresTopLevel3PDatasource: true,
	products: taskTypeProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'taskTypes',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchDynamicTaskTypeOptions(config);
		},
	},
});
